import { Link } from "@tanstack/react-router";
import { useEffect, useState } from "react";

type BrandRow = {
  slug: string;
  name: string;
  service_path: string;
  logo_url: string | null;
  logo_height: number | null;
};

import sunbriteLogo from "@/assets/sunbrite-logo.png.asset.json";
import araknisLogo from "@/assets/araknis-logo.jpg.asset.json";
import lgLogo from "@/assets/lg-logo.png.asset.json";
import sonosLogo from "@/assets/sonos-logo.png.asset.json";
import samsungLogo from "@/assets/samsung-logo.png.asset.json";
import marantzLogo from "@/assets/marantz-logo.jpeg.asset.json";
import lumaLogo from "@/assets/luma-logo.png.asset.json";

export type BrandKey = string;

type BrandItem = {
  key: string;
  name: string;
  to: string;
  logo?: string;
  logoHeight?: number;
};

// Static fallback rendered during SSR / before DB brands load.
const FALLBACK: BrandItem[] = [
  { key: "sonos", name: "Sonos", to: "/services/whole-home-audio", logo: sonosLogo.url, logoHeight: 28 },
  { key: "klipsch", name: "Klipsch", to: "/services/whole-home-audio" },
  { key: "episode", name: "Episode", to: "/services/whole-home-audio" },
  { key: "marantz", name: "Marantz", to: "/services/home-theater", logo: marantzLogo.url, logoHeight: 28 },
  { key: "denon", name: "Denon", to: "/services/home-theater" },
  { key: "sony", name: "Sony", to: "/services/whole-home-video" },
  { key: "lg", name: "LG", to: "/services/whole-home-video", logo: lgLogo.url, logoHeight: 28 },
  { key: "samsung", name: "Samsung", to: "/services/whole-home-video", logo: samsungLogo.url, logoHeight: 28 },
  { key: "sanus", name: "Sanus", to: "/services/tv-mounting" },
  { key: "sunbrite", name: "SunBrite", to: "/services/outdoor", logo: sunbriteLogo.url, logoHeight: 24 },
  { key: "eero", name: "eero", to: "/services/networking" },
  { key: "ubiquiti", name: "Ubiquiti", to: "/services/networking" },
  { key: "araknis", name: "Araknis Networks", to: "/services/networking", logo: araknisLogo.url, logoHeight: 28 },
  { key: "ecobee", name: "ecobee", to: "/services/climate-control" },
  { key: "luma", name: "Luma", to: "/services/security", logo: lumaLogo.url, logoHeight: 28 },
  { key: "wattbox", name: "WattBox", to: "/services/networking" },
];

type Props = {
  brands?: string[];
  eyebrow?: string;
  title?: string;
  subtitle?: string;
  variant?: "section" | "inline" | "marquee";
  showCta?: boolean;
};

export function BrandStrip({
  brands,
  eyebrow = "Authorized installer",
  title = "Brands we install.",
  subtitle = "We design and install with the most trusted names in audio, video, networking, and smart home — chosen for performance, reliability, and serviceability.",
  variant = "section",
  showCta = false,
}: Props) {
  const [items, setItems] = useState<BrandItem[]>(FALLBACK);

  useEffect(() => {
    // Plain PostgREST fetch instead of the supabase-js client: this component
    // renders on public marketing pages, and importing supabase-js here pulled
    // ~200 KB of auth/realtime JS into the homepage critical path.
    let cancelled = false;
    const url = import.meta.env.VITE_SUPABASE_URL;
    const key = import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY;
    if (!url || !key) return;
    const query =
      "select=slug,name,service_path,logo_url,logo_height,sort_order,enabled&enabled=eq.true&order=sort_order.asc";
    fetch(`${url}/rest/v1/brands?${query}`, {
      headers: { apikey: key, authorization: `Bearer ${key}` },
    })
      .then((r) => (r.ok ? r.json() : null))
      .then((data: BrandRow[] | null) => {
        if (cancelled || !data || data.length === 0) return;
        setItems(
          data.map((b) => ({
            key: b.slug,
            name: b.name,
            to: b.service_path,
            logo: b.logo_url ?? undefined,
            logoHeight: b.logo_height ?? 28,
          })),
        );
      })
      .catch(() => {
        /* keep static fallback */
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const filtered = brands && brands.length > 0
    ? brands.map((k) => items.find((i) => i.key === k)).filter(Boolean) as BrandItem[]
    : items;

  if (variant === "marquee" || variant === "inline") {
    return (
      <div className="rounded-xl border border-border bg-card/40 backdrop-blur p-6 md:p-8">
        {(eyebrow || title) && (
          <div className="mb-5">
            {eyebrow && <span className="eyebrow text-brand-text">{eyebrow}</span>}
            {title && <h3 className="mt-2 font-display text-xl md:text-2xl font-bold">{title}</h3>}
          </div>
        )}
        <BrandGrid items={filtered} compact />
      </div>
    );
  }

  return (
    <section className="container mx-auto px-6 py-24 border-t border-border">
      <div className="max-w-3xl mx-auto text-center">
        <span className="eyebrow text-brand-text">{eyebrow}</span>
        <h2 className="mt-3 font-display text-4xl md:text-5xl font-bold">{title}</h2>
        <p className="mt-6 text-muted-foreground text-lg leading-relaxed">{subtitle}</p>
      </div>
      <div className="mt-14">
        <BrandGrid items={filtered} />
      </div>
      {showCta && (
        <div className="mt-12 text-center">
          <Link
            to="/contact"
            className="inline-flex items-center gap-2 text-sm font-semibold text-brand-text hover:gap-3 transition-all"
          >
            Ask about a specific brand →
          </Link>
        </div>
      )}
    </section>
  );
}

function BrandGrid({
  items,
  compact = false,
}: {
  items: BrandItem[];
  compact?: boolean;
}) {
  return (
    <div className="relative overflow-hidden">
      <ul className="grid grid-cols-2 gap-3 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5">
        {items.map((b) => (
          <li key={b.key}>
            <a
              href={b.to}
              aria-label={`${b.name} — view related service`}
              className={`${compact ? "h-14 px-4" : "h-16 px-4 md:h-20 md:px-6"} group flex w-full items-center justify-center rounded-lg border border-border bg-brand-tile text-center shadow-sm hover:border-brand/60 hover:shadow-md transition`}
            >
              {b.logo ? (
                <img
                  src={b.logo}
                  alt={b.name}
                  className="max-w-full object-contain"
                  style={{ height: b.logoHeight ?? 24, maxHeight: compact ? 32 : 40 }}
                  width={120}
                  height={b.logoHeight ?? 24}
                  loading="lazy"
                  decoding="async"
                />

              ) : (
                <span className="font-display text-base md:text-lg font-semibold text-brand-tile-foreground tracking-wide transition">
                  {b.name}
                </span>
              )}
            </a>
          </li>
        ))}
      </ul>
    </div>
  );
}
